import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import React, { useContext, useEffect, useState } from 'react';
import { TbFileSearch } from 'react-icons/all';
import { MultiSelect } from 'react-multi-select-component';
import { Button, Datepicker, Heading, ReactSelect } from '~/components';
import { Card } from '~/components/Card';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import {
  useCompanyGroupsAsSelect,
  useFilialAsSelect,
  useMatrizAsSelect,
} from '~/hooks';
import { getContractMeasurementXlsxCsv } from '~/services/ContractMeasurement';
import { SelectItem } from '~/types';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';
import { getLastDayOfMonthAfterXMonths } from '~/utils/getLastDayOfMonthAfterXMonths';
import { FileStatus } from '~/pages/Portal/ContractMeasurement/FileStatus';

export const ContractMeasurement: React.VFC = () => {
  const toast = useToast();

  const [selectedGroups, setSelectedGroups] = useState<SelectItem[]>([]);
  const [selectedHeadOffices, setSelectedHeadOffices] = useState<SelectItem[]>(
    [],
  );
  const [selectedBranches, setSelectedBranches] = useState<SelectItem[]>([]);

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);

  const [dataInicial, setDataInicial] = useState<Date | undefined>(() => {
    const data = new Date();
    return data || undefined;
  });
  const [dataFinal, setDataFinal] = useState<Date | undefined>(() => {
    return getLastDayOfMonthAfterXMonths(new Date(), 12);
  });

  const [maxDataFinal, setMaxDataFinal] = useState<Date | undefined>(() => {
    return getLastDayOfMonthAfterXMonths(new Date(), 12);
  });

  const [relatorio, setRelatorio] = useState<number>(0);

  const [tipoArquivo, setTipoArquivo] = useState<number>(0);

  const { groups: groupsFilter } = useContext(FilterContext);
  const { headOffices: headOfficesFilter } = useContext(FilterContext);
  const { branches: branchesFilter } = useContext(FilterContext);

  const { data: groups, isLoading: isGroupsLoading } = useCompanyGroupsAsSelect(
    {},
    9999,
    groupsFilter,
  );
  const { data: headOffices, isLoading: isHeadOfficesLoading } =
    useMatrizAsSelect({}, headOfficesFilter, selectedGroups);

  const { data: branches, isLoading: isBranchesLoading } = useFilialAsSelect(
    {},
    branchesFilter,
    selectedGroups,
    selectedHeadOffices,
  );

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getContractMeasurementXlsxCsv(
      tipoArquivo === 0 ? 'csv' : 'xlsx',
      selectedGroups.length > 0 ? selectedGroups : groupsFilter,
      selectedGroups.length > 0 ? selectedHeadOffices : headOfficesFilter,
      selectedGroups.length > 0 ? selectedBranches : branchesFilter,
      relatorio,
      dataInicial,
      dataFinal,
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const handlePeriodoInicial = (date: Date | null, tpArquivo?: number) => {
    let data_final = date;
    const tpArquivoFinal = tpArquivo ?? tipoArquivo;
    if (data_final) {
      data_final = getLastDayOfMonthAfterXMonths(
        data_final,
        tpArquivoFinal ? (tpArquivoFinal === 1 ? 6 : 12) : 12,
      );
    }
    setDataInicial(date ?? undefined);
    setDataFinal(data_final ?? undefined);
    setMaxDataFinal(data_final ?? undefined);
  };

  const handlePeriodoFinal = (date: Date | null) => {
    setDataFinal(date ?? undefined);
  };

  const handleSelectedGroupsChange = async (itens?: SelectItem[]) => {
    setSelectedGroups(itens || []);
    setSelectedHeadOffices([]);
    setSelectedBranches([]);
  };

  const handleSelectedHeadOfficesChange = async (itens?: SelectItem[]) => {
    setSelectedHeadOffices(itens || []);
    setSelectedBranches([]);
  };

  const handleSelectedBranchesChange = async (itens?: SelectItem[]) => {
    setSelectedBranches(itens || []);
  };

  const handleRelatorio = (e: number) => {
    setRelatorio(e);
  };

  const handleTipoArquivo = (e: number) => {
    setTipoArquivo(e);
    if (selectedGroups.length > 1) {
      setSelectedGroups([]);
      setSelectedHeadOffices([]);
      setSelectedBranches([]);
    }
  };

  return (
    <Box px="8" py="4">
      <Heading>Medição de Contratos</Heading>
      <HStack pt={2} w="100%" align="end" mt={3}>
        <FormControl w="20%">
          <FormLabel fontSize="14px">Relatório</FormLabel>
          <RadioGroup
            colorScheme="b"
            value={relatorio}
            onChange={e => handleRelatorio(Number(e))}
          >
            <HStack gridGap={4}>
              <Radio value={0}>Sintético</Radio>
              <Radio value={1}>Analítico</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
        <FormControl w="20%">
          <FormLabel fontSize="14px">
            Tipo de arquivo&ensp;
            <FileStatus relatorio={relatorio} tipo={tipoArquivo} />
          </FormLabel>
          <RadioGroup
            colorScheme="b"
            value={tipoArquivo}
            onChange={e => {
              handleTipoArquivo(Number(e));
              dataInicial && handlePeriodoInicial(dataInicial, Number(e));
            }}
          >
            <HStack gridGap={4}>
              <Radio value={0}>CSV</Radio>
              <Radio value={1}>Excel</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
      </HStack>
      <HStack
        pt={2}
        w="100%"
        align="end"
        mt={3}
        mb={5}
        flexWrap="wrap"
        gridGap={2}
      >
        <HStack align="end" gridGap={2} width="30%" maxW="400px" minW="400px">
          <Datepicker
            label="Período de análise"
            required
            onChange={e => handlePeriodoInicial(e)}
            selectedDate={dataInicial || undefined}
            isClearable
            maxDate={maxDataFinal}
            minDate={new Date(2022, 0, 1)}
            helpText={
              tipoArquivo === 0
                ? 'Período de análise disponivel de até 12 meses.'
                : 'Período de análise disponivel de até 6 meses.'
            }
          />
          <Text>a</Text>
          <Datepicker
            onChange={e => handlePeriodoFinal(e)}
            selectedDate={dataFinal || undefined}
            isClearable
            minDate={dataInicial || undefined}
            maxDate={maxDataFinal}
            isDisabled={!dataInicial}
          />
        </HStack>
        <FormControl width="300px" maxW="300px" minW="250px">
          <FormLabel fontSize="14px">
            {tipoArquivo === 1 ? (
              <Text>
                Selecione um Grupo Econômico{' '}
                <span style={{ color: 'red' }}>&ensp;* </span>
              </Text>
            ) : (
              <Text>Selecione um ou mais Grupos Econômicos </Text>
            )}
          </FormLabel>
          {tipoArquivo === 1 && (
            <ReactSelect
              defaultStyle
              isSearchable
              placeholder="Selecione"
              options={groups || []}
              value={selectedGroups}
              isClearable
              onChange={changes =>
                handleSelectedGroupsChange(
                  changes ? ([changes] as SelectItem[]) : [],
                )
              }
            />
          )}
          {tipoArquivo === 0 && (
            <MultiSelect
              value={selectedGroups}
              options={groups || []}
              onChange={(changes: SelectItem[]) =>
                handleSelectedGroupsChange(changes)
              }
              labelledBy="Selecione"
              overrideStrings={MultiSelectStrings}
              isLoading={isGroupsLoading}
            />
          )}
        </FormControl>
        <FormControl width="300px" maxW="300px" minW="250px">
          <FormLabel fontSize="14px">Selecione uma ou mais Matrizes</FormLabel>
          <MultiSelect
            value={selectedHeadOffices}
            options={headOffices || []}
            onChange={(changes: SelectItem[]) =>
              handleSelectedHeadOfficesChange(changes)
            }
            labelledBy="Selecione"
            overrideStrings={MultiSelectStrings}
            disabled={!selectedGroups.length}
            isLoading={isHeadOfficesLoading}
          />
        </FormControl>
        <FormControl width="300px" maxW="300px" minW="250px">
          <FormLabel fontSize="14px">Selecione uma ou mais Filiais</FormLabel>
          <MultiSelect
            value={selectedBranches}
            options={branches || []}
            onChange={(changes: SelectItem[]) =>
              handleSelectedBranchesChange(changes)
            }
            labelledBy="Selecione"
            overrideStrings={MultiSelectStrings}
            disabled={!selectedGroups.length}
            isLoading={isBranchesLoading}
          />
        </FormControl>
        <Button
          marginTop="auto"
          disabled={
            isLoadingDownload ||
            !dataInicial ||
            !dataFinal ||
            (!selectedGroups.length && tipoArquivo === 1)
          }
          onClick={handleDownload}
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          width="150px"
        >
          Gerar
          {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
        </Button>
      </HStack>
      <Card
        width="100%"
        content={
          <Stack alignItems="center" gridGap={2} m={5}>
            <Icon as={TbFileSearch} fontSize="50px" />
            <Text textAlign="center">
              Informações de contratos disponibilizadas a partir de 2022.
              <br /> Período de extração da base será limitada ao período de{' '}
              {tipoArquivo === 0 ? '12 ' : '6 '}
              meses.
              <br /> Selecione o período para gerar o relatório.
            </Text>
          </Stack>
        }
      />
    </Box>
  );
};
